import { Link } from '@tanstack/react-router';
import {
  ArrowRight, Banknote, BarChart3, Boxes, CheckCircle2, Factory,
  Globe2, Receipt, ShieldCheck, Smartphone, Sparkles, Store, Users, Wrench,
} from 'lucide-react';
import { MarketingShell } from './MarketingShell';
import { PlansPreview } from './PlansPreview';
import { Reveal } from './Reveal';

/**
 * Possignin product landing — possignin.com. Possignin's own brand. Editorial
 * minimalism in the Stripe / Linear / Mercury family: one indigo accent, zinc
 * neutrals, generous whitespace, precise type, one signature moment (the hero
 * product panel). Real content (cloud POS for Bangladesh by ITCity); light +
 * dark; mobile-first. These sections map to the CMS schema later (WS5b).
 */
export function PlatformLanding() {
  return (
    <MarketingShell>
      <Hero />
      <TrustBar />
      <Features />
      <BuiltForBD />
      <HowItWorks />
      <PlansPreview />
      <ResellerBand />
      <Testimonial />
      <Faq />
      <FinalCta />
    </MarketingShell>
  );
}

/* ------------------------------------------------------------------ Hero -- */

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-zinc-200/70 dark:border-zinc-800/70">
      {/* Single soft radial backlight — the only "atmosphere" on the page. */}
      <div className="glow-bg absolute inset-x-0 top-0 h-[36rem] -z-10 pointer-events-none" aria-hidden="true" />
      {/* Hairline grid texture, barely there. */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.4] dark:opacity-[0.25] pointer-events-none [mask-image:radial-gradient(60%_45%_at_50%_0%,black,transparent)]"
        aria-hidden="true"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgb(228 228 231 / 0.6) 1px, transparent 1px), linear-gradient(to bottom, rgb(228 228 231 / 0.6) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center">
        <Reveal>
          <Link
            to="/blog"
            className="group inline-flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full border border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:border-zinc-300 dark:hover:border-zinc-700 transition-colors shadow-xs"
          >
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-subtle text-brand text-[11px] font-semibold dark:bg-brand-dm-subtle dark:text-brand-dm">
              <Sparkles className="w-3 h-3" /> New
            </span>
            Customer ledgers &amp; cash collection
            <ArrowRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </Reveal>

        <Reveal delay={60}>
          <h1 className="mt-6 text-[2.5rem] leading-[1.04] sm:text-6xl lg:text-[4rem] font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-3xl mx-auto text-balance">
            The POS that runs your{' '}
            <span className="text-brand dark:text-brand-dm">whole business</span>.
          </h1>
        </Reveal>

        <Reveal delay={120}>
          <p className="mt-5 text-lg sm:text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed text-pretty">
            Sales, inventory, HRM, CRM, manufacturing, and repair — one calm system, priced in
            taka. Built in Bangladesh, for the way Bangladesh does business.
          </p>
        </Reveal>

        <Reveal delay={180}>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/signup"
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-white bg-brand hover:bg-brand-hover shadow-sm transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover"
            >
              Start your free trial
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/pricing"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition-colors"
            >
              See pricing
            </Link>
          </div>
        </Reveal>

        <Reveal delay={240}>
          <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-sm text-zinc-600 dark:text-zinc-300">
            {['7-day free trial', 'No card required', 'Setup in 5 minutes', 'Bangla + English'].map((t) => (
              <li key={t} className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" /> {t}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>

      {/* Signature moment: the product panel (the "Mercury pattern", used once). */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20">
        <Reveal delay={120} y={28}>
          <ProductPanel />
        </Reveal>
      </div>
    </section>
  );
}

/** A faithful-feeling, hand-built mock of the Possignin POS sell screen. */
function ProductPanel() {
  const lines = [
    { name: 'Singara', qty: 12, price: '120.00' },
    { name: 'Cha (milk)', qty: 8, price: '160.00' },
    { name: 'Cold drink 250ml', qty: 4, price: '140.00' },
    { name: 'Bread, large', qty: 2, price: '110.00' },
  ];
  return (
    <div className="rounded-2xl border border-zinc-200/80 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/60 backdrop-blur-sm p-2 sm:p-3 shadow-lg">
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/80 dark:bg-zinc-900/60">
          <span className="flex gap-1.5" aria-hidden="true">
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-zinc-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-zinc-700" />
            <span className="w-2.5 h-2.5 rounded-full bg-zinc-300 dark:bg-zinc-700" />
          </span>
          <span className="ml-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">Possignin · Sell</span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> Online
          </span>
        </div>

        <div className="grid sm:grid-cols-[1fr_15rem]">
          {/* receipt / cart */}
          <div className="p-4 sm:p-5 border-b sm:border-b-0 sm:border-r border-zinc-200 dark:border-zinc-800">
            <div className="flex items-center justify-between text-xs text-zinc-500 dark:text-zinc-400">
              <span className="font-medium uppercase tracking-wider">Current sale</span>
              <span className="mono tabular-nums">INV-2026-0345</span>
            </div>
            <ul className="mt-3 divide-y divide-zinc-100 dark:divide-zinc-800/80">
              {lines.map((l) => (
                <li key={l.name} className="flex items-center gap-3 py-2.5">
                  <span className="w-7 h-7 shrink-0 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-[11px] font-semibold tabular-nums text-zinc-600 dark:text-zinc-300">
                    {l.qty}
                  </span>
                  <span className="flex-1 text-sm font-medium text-zinc-800 dark:text-zinc-100 truncate">{l.name}</span>
                  <span className="text-sm tabular-nums text-zinc-600 dark:text-zinc-300">৳ {l.price}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-zinc-800 flex items-end justify-between">
              <span className="text-sm text-zinc-500 dark:text-zinc-400">Total</span>
              <span className="text-2xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">৳ 530.00</span>
            </div>
          </div>

          {/* keypad / actions */}
          <div className="p-4 sm:p-5 bg-zinc-50/60 dark:bg-zinc-900/40">
            <div className="grid grid-cols-3 gap-2" aria-hidden="true">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k) => (
                <span key={k} className="h-9 grid place-items-center rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-sm font-medium tabular-nums text-zinc-700 dark:text-zinc-300">
                  {k}
                </span>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <span className="h-9 grid place-items-center rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-xs font-medium text-zinc-600 dark:text-zinc-300">Cash</span>
              <span className="h-9 grid place-items-center rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 text-xs font-medium text-zinc-600 dark:text-zinc-300">Card</span>
            </div>
            <span className="mt-2 flex h-10 items-center justify-center rounded-md bg-brand text-white text-sm font-semibold dark:bg-brand-dm dark:text-zinc-950">
              Charge ৳ 530.00
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- TrustBar -- */

function TrustBar() {
  const stats = [
    { value: '106', label: 'businesses running' },
    { value: '4.9 GiB', label: 'live transaction data' },
    { value: '৳', label: 'native BDT billing' },
    { value: '24/7', label: 'support from Sylhet' },
  ];
  return (
    <section className="border-b border-zinc-200/70 dark:border-zinc-800/70 bg-white/50 dark:bg-zinc-900/30">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-9 grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-200/70 dark:divide-zinc-800/70">
        {stats.map((s, i) => (
          <Reveal key={s.label} delay={i * 60} className="text-center px-3">
            <div className="text-2xl sm:text-3xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">{s.value}</div>
            <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{s.label}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- Features -- */

const FEATURES = [
  { icon: Boxes, title: 'Sales & inventory', body: 'Ring up sales fast, track every item. FIFO, lot tracking, barcodes, and stock alerts out of the box.' },
  { icon: Banknote, title: 'BDT-native billing', body: 'SSLCommerz built in, cash-to-office in Sylhet, auto invoices and receipts — all in taka.' },
  { icon: Users, title: 'HRM & payroll', body: 'Staff, shifts, attendance, leave, and payroll — the people side of your shop, handled.' },
  { icon: Store, title: 'CRM & contacts', body: 'Customers, suppliers, dues, and follow-ups. Know who owes what at a glance.' },
  { icon: Factory, title: 'Manufacturing', body: 'Recipes, ingredients, and production runs for bakeries, kitchens, and workshops.' },
  { icon: Wrench, title: 'Repair & service', body: 'Job sheets, device tracking, and service status for repair shops and workshops.' },
  { icon: BarChart3, title: 'Reports that read', body: 'Daily sales, P&L, stock, and tax — plain reports you can act on. Export any time.' },
  { icon: Smartphone, title: 'Runs on a phone', body: 'Android, iPhone, or any browser. Receipt printers and barcode scanners supported.' },
  { icon: ShieldCheck, title: 'Calm & secure', body: '2FA, audit logs, role-based access. Your data lives in your own database — never sold.' },
];

function Features() {
  return (
    <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">Everything in one place</p>
        <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
          One system instead of six.
        </h2>
        <p className="mt-3 text-zinc-600 dark:text-zinc-300 leading-relaxed text-pretty">
          Most shops juggle a register, a spreadsheet, a notebook, and a chat group. Possignin
          replaces all of it with software that actually fits how you work.
        </p>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-200/70 dark:bg-zinc-800/70">
        {FEATURES.map((f, i) => (
          <Reveal
            key={f.title}
            delay={(i % 3) * 60}
            className="group bg-white dark:bg-zinc-950 p-6 transition-colors hover:bg-zinc-50/80 dark:hover:bg-zinc-900/60"
          >
            <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm transition-transform duration-200 group-hover:scale-105">
              <f.icon className="w-5 h-5" aria-hidden="true" />
            </span>
            <h3 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{f.title}</h3>
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{f.body}</p>
          </Reveal>
        ))}
      </div>
      <div className="mt-8">
        <Link
          to="/features"
          className="group inline-flex items-center gap-1.5 text-sm font-medium text-brand dark:text-brand-dm"
        >
          See all features
          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ BuiltForBD -- */

function BuiltForBD() {
  const items = [
    { icon: Banknote, title: 'Taka, not dollars', body: 'Every price, invoice, and report is in BDT. SSLCommerz for online; cash collection to our Sylhet office for the rest.' },
    { icon: Globe2, title: 'Bangla + English', body: 'Switch language per user. Receipts, reports, and the POS follow — no half-translated screens.' },
    { icon: Receipt, title: 'Made for local rules', body: 'VAT-ready invoicing and the receipt formats your customers expect. No Western-only assumptions.' },
  ];
  return (
    <section className="border-y border-zinc-200/70 dark:border-zinc-800/70 bg-white/50 dark:bg-zinc-900/30">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid lg:grid-cols-3 gap-10 lg:gap-12">
          <Reveal className="lg:col-span-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">Built for Bangladesh</p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 leading-tight text-balance">
              Not a Western tool, translated.
            </h2>
            <p className="mt-3 text-zinc-600 dark:text-zinc-300 leading-relaxed text-pretty">
              Possignin was built in Sylhet for shops here — the currency, the languages, the
              payment habits, and the support are all local.
            </p>
          </Reveal>
          <div className="lg:col-span-2 grid sm:grid-cols-3 gap-4">
            {items.map((it, i) => (
              <Reveal
                key={it.title}
                delay={i * 70}
                className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-5"
              >
                <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm">
                  <it.icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <h3 className="mt-3 font-semibold text-zinc-950 dark:text-zinc-50">{it.title}</h3>
                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{it.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ HowItWorks -- */

function HowItWorks() {
  const steps = [
    { n: '1', title: 'Sign up free', body: 'Create your business in minutes — no card, no sales call. Pick a plan when the trial ends.' },
    { n: '2', title: 'Set up your shop', body: 'Add products, staff, and locations. Import a CSV or start fresh. We help if you get stuck.' },
    { n: '3', title: 'Start selling', body: 'Ring up sales on any device. Watch the reports fill in. Pay in taka, online or cash.' },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <Reveal className="text-center max-w-2xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">How it works</p>
        <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
          Live by this afternoon.
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-3 gap-8 sm:gap-6 relative">
        {/* connecting hairline on desktop */}
        <div className="hidden sm:block absolute left-[16%] right-[16%] top-4 h-px bg-zinc-200 dark:bg-zinc-800" aria-hidden="true" />
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 80} className="relative text-center sm:text-left">
            <span className="relative z-10 w-9 h-9 grid place-items-center rounded-full bg-brand text-white text-sm font-semibold tabular-nums shadow-sm dark:bg-brand-dm dark:text-zinc-950 mx-auto sm:mx-0 ring-4 ring-marketing dark:ring-marketing-dark">
              {s.n}
            </span>
            <h3 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{s.title}</h3>
            <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed max-w-xs mx-auto sm:mx-0">{s.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- ResellerBand -- */

function ResellerBand() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
      <Reveal>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950 dark:bg-zinc-900 p-8 sm:p-14 text-center overflow-hidden relative">
          <div
            className="absolute inset-0 -z-0 opacity-40 pointer-events-none"
            style={{ background: 'radial-gradient(40rem 22rem at 50% 120%, rgb(99 102 241 / 0.45), transparent 70%)' }}
            aria-hidden="true"
          />
          <div className="relative">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-dm">Partner program</p>
            <h2 className="mt-2 text-2xl sm:text-4xl font-semibold tracking-tight text-white max-w-2xl mx-auto text-balance">
              Sell Possignin under your own brand.
            </h2>
            <p className="mt-4 text-zinc-300 max-w-xl mx-auto leading-relaxed text-pretty">
              Become a reseller and earn commission on every business you bring in. Your domain,
              your branding, your pricing — our infrastructure. Ogrogoti and Nexico already do.
            </p>
            <Link
              to="/become-a-reseller"
              className="group mt-7 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand-hover transition-colors"
            >
              Become a reseller
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ------------------------------------------------------------ Testimonial -- */

function Testimonial() {
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">Why shops switch</p>
        <p className="mt-5 text-2xl sm:text-[1.75rem] font-medium tracking-tight text-zinc-950 dark:text-zinc-50 leading-snug text-balance">
          Most shops come to Possignin from a notebook and a spreadsheet. They stay because the numbers
          finally live in one place — and the evening close takes minutes, not hours.
        </p>
        <p className="mt-6 text-sm text-zinc-500 dark:text-zinc-400">
          106 businesses across Bangladesh run on Possignin — directly and through resellers.
        </p>
      </Reveal>
    </section>
  );
}

/* -------------------------------------------------------------------- Faq -- */

function Faq() {
  const rows = [
    { q: 'Do I need a card to start?', a: 'No. The 7-day trial needs no card. You pick a plan and pay in taka only when the trial ends.' },
    { q: 'Can I use it in Bangla?', a: 'Yes. Each user can switch between Bangla and English; receipts and reports follow the choice.' },
    { q: 'How do I pay?', a: 'SSLCommerz for online payments, or cash to our Sylhet office. Your subscription stays active either way.' },
    { q: 'Where does my data live?', a: 'In your own database. We never sell your data, and you can export it any time.' },
    { q: 'What if I sell software too?', a: 'Apply to our reseller program and sell Possignin under your own brand and pricing.' },
  ];
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <Reveal>
        <h2 className="text-center text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
          Questions, answered.
        </h2>
      </Reveal>
      <dl className="mt-8 divide-y divide-zinc-200 dark:divide-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
        {rows.map((r) => (
          <details key={r.q} className="group px-5 py-4 sm:px-6 [&_summary::-webkit-details-marker]:hidden" open>
            <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-zinc-950 dark:text-zinc-50">
              {r.q}
              <ArrowRight className="w-4 h-4 shrink-0 text-zinc-400 transition-transform duration-200 group-open:rotate-90" aria-hidden="true" />
            </summary>
            <dd className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{r.a}</dd>
          </details>
        ))}
      </dl>
    </section>
  );
}

/* --------------------------------------------------------------- FinalCta -- */

function FinalCta() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
      <Reveal>
        <div className="relative rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-10 sm:p-16 text-center overflow-hidden">
          <div className="glow-bg absolute inset-x-0 top-0 h-40 pointer-events-none" aria-hidden="true" />
          <div className="relative">
            <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
              Run your shop the calm way.
            </h2>
            <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-300 max-w-md mx-auto text-pretty">
              Free for 7 days. Setup takes five minutes. No card needed.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/signup"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-white bg-brand hover:bg-brand-hover shadow-sm transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover"
              >
                Start your free trial
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/pricing"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
              >
                Compare plans
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
